import React, {createContext, useContext, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {useFormik, FormikProvider} from "formik";
import moment from "moment";
import * as actions from '../actions/actions';
import * as usersActions from '../../../actions/general/getUsers';
import * as statusActions from '../../../actions/general/getNoteStatusTypes';
import {getOption} from "../../../../modules/Inspection/utils/getOption";

const emptyNote = {
  generalNoteID: 0,
  shumaID: 0,
  responsibleUserID: 0,
  createDate: new Date(),
  statusTypeID: 0,
  statusTypeName: '',
  dueDate: new Date(),
  freeText: '',
};

const PopupNotesContext = createContext({
  list: [],
  details: emptyNote,
  loading: false,
  updateList: () => {
  },
  getDetails: () => {
  },
  resetDetails: () => {
  },
  createNote: () => {
  },
  editNote: () => {
  },
  deleteNote: () => {
  },
});

export const usePopupNotesContext = () => useContext(PopupNotesContext);

export const PopupNotesContextProvider = ({children, shumaID}) => {
  const [list, setList] = useState([]);
  const [details, setDetails] = useState(emptyNote);
  const dispatch = useDispatch();
  const {users, noteStatusTypes} = useSelector((state) => ({
    users: state?.firmUsersReducer?.users,
    noteStatusTypes: state?.generalNoteStatusTypesReducer?.generalNoteStatusTypes,
  }), shallowEqual);

  const updateList = async () => {
    try {
      const list = await actions.getList(shumaID);
      setList([...list]);
    } catch (e) {
      console.error(e);
      setList([]);
    }
  };

  const getDetails = async (id) => {
    if (id !== -1) {
      try {
        const data = await actions.getDetails(id);
        setDetails(data);
      } catch (e) {
        console.error(e);
        setDetails(emptyNote);
      }
    }
  };

  const resetDetails = (id) => {
    if (id === -1) {
      setDetails(emptyNote);
    }
  };

  const createNote = async (
    statusTypeID,
    dueDate,
    responsibleUserID,
    freeText
  ) => {
    try {
      await actions.addEditNote({
        generalNoteID: -1,
        shumaID,
        statusTypeID: statusTypeID?.value ?? null,
        dueDate,
        responsibleUserID: responsibleUserID?.value ?? null,
        freeText
      });
    } catch (e) {
      console.error(e);
    }
  };

  const editNote = async (
    generalNoteID,
    statusTypeID,
    dueDate,
    responsibleUserID,
    freeText
  ) => {
    try {
      await actions.addEditNote({
        generalNoteID,
        shumaID,
        statusTypeID: statusTypeID?.value ?? null,
        dueDate,
        responsibleUserID: responsibleUserID?.value ?? null,
        freeText
      });
    } catch (e) {
      console.error(e);
    }
  };

  const deleteNote = async (id) => {
    try {
      await actions.deleteNote(id);
    } catch (e) {
      console.error(e);
    }
  };

  const formik = useFormik({
    initialValues: {
      generalNoteID: details?.generalNoteID || -1,
      responsibleUserID: getOption(users, details?.responsibleUserID),
      dueDate: details?.dueDate && details.generalNoteID ? moment(details.dueDate).toDate() : null,
      statusTypeID: getOption(noteStatusTypes, details?.statusTypeID),
      freeText: details?.freeText || '',
    },
    onSubmit: () => {
    },
    validate: (values) => {
      let errors = {};
      if (!values?.statusTypeID?.value) errors['statusTypeID'] = 'Required field';
      return errors;
    },
    validateOnMount: true,
    enableReinitialize: true,
  });

  const getOptions = () => {
    dispatch(usersActions.getFirmUsers());
    dispatch(statusActions.getNoteStatusTypes());
  };

  const resultList = [
    ...list.map((item) => {
      return {
        ...item,
        onOpen: async () => {
          formik.resetForm();
          await getDetails(item.generalNoteID);
          getOptions();
        },
        onEdit: async () => {
          await editNote(
            item.generalNoteID,
            formik.values.statusTypeID,
            formik.values.dueDate,
            formik.values.responsibleUserID,
            formik.values.freeText
          );
          await updateList();
        },
        onDelete: async () => {
          await deleteNote(item.generalNoteID);
          await updateList();
        }
      };
    })
  ];

  const resetNoteDetails = () => {
    formik.setFieldValue('generalNoteID', -1);
    formik.setFieldValue('responsibleUserID', null);
    formik.setFieldValue('dueDate', null);
    formik.setFieldValue('statusTypeID', null);
    formik.setFieldValue('freeText', '');
  }

  return (
    <PopupNotesContext.Provider value={{
      list: resultList,
      details,
      updateList,
      getDetails,
      resetDetails,
      createNote,
      editNote,
      deleteNote,
      formik,
      disabled: !Boolean(shumaID) || (shumaID === -1),
      shumaID,
      resetNoteDetails,
      getOptions
    }}>
      <FormikProvider value={formik}>
        {children}
      </FormikProvider>
    </PopupNotesContext.Provider>
  );
};